import { useEffect, useState } from 'react'
import { router } from '@inertiajs/react'
import Monogram from './Monogram'
import { track } from '../lib/events'

const STATUS_LABELS = [
  ['scheduled', 'Por jogar'],
  ['live', 'Ao vivo'],
  ['finished', 'Terminado'],
]

export default function MatchEditor({ match, editionSlug, onSaved }) {
  const [home, setHome] = useState(match.homeScore ?? 0)
  const [away, setAway] = useState(match.awayScore ?? 0)
  const [status, setStatus] = useState(match.status || 'scheduled')
  const [saving, setSaving] = useState(false)
  const [homePens, setHomePens] = useState(match.homePenalties ?? 0)
  const [awayPens, setAwayPens] = useState(match.awayPenalties ?? 0)

  // Depend on the match object (new identity after every Inertia reload), not
  // just the id — otherwise the editor keeps stale scores after a save that
  // changed them server-side (e.g. reverting to "Por jogar" clears the result).
  useEffect(() => {
    setHome(match.homeScore ?? 0)
    setAway(match.awayScore ?? 0)
    setStatus(match.status || 'scheduled')
    setHomePens(match.homePenalties ?? 0)
    setAwayPens(match.awayPenalties ?? 0)
  }, [match])

  const hasTeams = match.home && match.away
  // Reverting a match with a saved result to "Por jogar" deletes that result.
  const removingResult = status === 'scheduled' && match.homeScore != null

  // Empate num jogo a eliminar resolve-se nas grandes penalidades (sem
  // prolongamento). 0–0 significa "ainda não registadas" — envia null.
  const drawnKnockout = hasTeams && match.stage !== 'group' && status !== 'scheduled' && home === away
  const pensEntered = drawnKnockout && (homePens !== 0 || awayPens !== 0)
  const pensTied = pensEntered && homePens === awayPens

  function clamp(n) {
    return Math.max(0, n)
  }

  function save() {
    setSaving(true)
    router.patch(
      `/matches/${match.id}`,
      {
        edition_slug: editionSlug,
        match: {
          home_score: home,
          away_score: away,
          home_penalties: pensEntered ? homePens : null,
          away_penalties: pensEntered ? awayPens : null,
          status,
        },
      },
      {
        preserveScroll: true,
        onFinish: () => setSaving(false),
        onSuccess: () => {
          track(removingResult ? 'match-result-removed' : 'match-result-saved', {
            game: match.gameNumber,
          })
          onSaved && onSaved()
        },
      }
    )
  }

  return (
    <div className="editor">
      <div className="et">● A editar · Jogo {match.gameNumber} · {match.field ? `Campo ${match.field}` : 'Eliminatória'}</div>
      <div className="ewhere">
        {match.label || (match.group ? `Grupo ${match.group}` : 'Jogo')} — {match.time}
      </div>

      {hasTeams ? (
        <div className="scorebox">
          <div className="tcol">
            <Monogram team={match.home} size={46} />
            <span>{match.home.shortName}</span>
            <div className="stepper">
              <button type="button" onClick={() => setHome((n) => clamp(n - 1))}>−</button>
              <span className="val">{home}</span>
              <button type="button" onClick={() => setHome((n) => clamp(n + 1))}>+</button>
            </div>
          </div>
          <div className="dash">–</div>
          <div className="tcol">
            <Monogram team={match.away} size={46} />
            <span>{match.away.shortName}</span>
            <div className="stepper">
              <button type="button" onClick={() => setAway((n) => clamp(n - 1))}>−</button>
              <span className="val">{away}</span>
              <button type="button" onClick={() => setAway((n) => clamp(n + 1))}>+</button>
            </div>
          </div>
        </div>
      ) : (
        <p className="ewhere" style={{ marginBottom: '22px' }}>
          As equipas serão definidas quando os jogos anteriores terminarem.
        </p>
      )}

      {drawnKnockout && (
        <div className="penbox">
          <div className="et">Grandes penalidades</div>
          <div className="scorebox">
            <div className="tcol">
              <span>{match.home.shortName}</span>
              <div className="stepper">
                <button type="button" onClick={() => setHomePens((n) => clamp(n - 1))}>−</button>
                <span className="val">{homePens}</span>
                <button type="button" onClick={() => setHomePens((n) => clamp(n + 1))}>+</button>
              </div>
            </div>
            <div className="dash">–</div>
            <div className="tcol">
              <span>{match.away.shortName}</span>
              <div className="stepper">
                <button type="button" onClick={() => setAwayPens((n) => clamp(n - 1))}>−</button>
                <span className="val">{awayPens}</span>
                <button type="button" onClick={() => setAwayPens((n) => clamp(n + 1))}>+</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="filters" style={{ marginBottom: '16px' }}>
        {STATUS_LABELS.map(([key, label]) => (
          <span
            key={key}
            className={`f ${status === key ? 'on' : ''}`}
            onClick={() => setStatus(key)}
          >
            {label}
          </span>
        ))}
      </div>

      <button className="save" type="button" onClick={save} disabled={saving || !hasTeams || pensTied}>
        {removingResult ? 'Remover resultado' : 'Gravar resultado'}
      </button>
      <div className="hint">
        {removingResult ? (
          <>O resultado será apagado e o jogo volta a <b>Por jogar</b>.</>
        ) : pensTied ? (
          <>As grandes penalidades <b>não podem terminar empatadas</b>.</>
        ) : drawnKnockout && !pensEntered ? (
          <>Empate — regista as <b>grandes penalidades</b> para apurar o vencedor.</>
        ) : (
          <>Ao gravar, atualiza <b>classificações</b> e <b>eliminatórias</b> automaticamente.</>
        )}
      </div>
    </div>
  )
}
